/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { updateExistingSalaryConfiguration } from "store/features/company/payrollManagement/salaryConfiguration/salaryConfigurationActions";
import { fetchSalaryStructures } from "store/features/company/payrollManagement/salaryStructure/salaryStructureActions";
import { selectSalaryStructures } from "store/features/company/payrollManagement/salaryStructure/salaryStructureSelectors";
import editSalaryConfigSvg from "assets/modals/edit.svg";

const EditSalaryConfigurationModal = ({
  isOpen,
  onClose,
  salaryConfig,
  onSuccess,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const salaryStructures = useSelector(selectSalaryStructures);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchSalaryStructures());
      const originalOverflow = document.body.style.overflow;
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = "0px";
      };
    }
  }, [isOpen, dispatch]);

  const [loading, setLoading] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [formData, setFormData] = useState({
    salary_structure_id: "",
    custom_basic_salary: "",
    custom_hra_amount: "",
    custom_conveyance_amount: "",
    custom_education_allowance_amount: "",
    custom_transport_allowance_amount: "",
    custom_fixed_allowance_amount: "",
    custom_gratuity_amount: "",
    custom_commission_amount: "",
    custom_fuel_reimbursement: "",
    custom_telephone_reimbursement: "",
    custom_driver_reimbursement: "",
    custom_vehicle_maintenance_reimbursement: "",
    custom_lta_reimbursement: "",
    additional_allowance_1_name: "",
    additional_allowance_1_amount: "",
    additional_allowance_2_name: "",
    additional_allowance_2_amount: "",
    additional_allowance_3_name: "",
    additional_allowance_3_amount: "",
    additional_deduction_1_name: "",
    additional_deduction_1_amount: "",
    additional_deduction_2_name: "",
    additional_deduction_2_amount: "",
    additional_deduction_3_name: "",
    additional_deduction_3_amount: "",
    effective_from: "",
    effective_to: "",
    status: "",
  });

  useEffect(() => {
    if (salaryConfig) {
      const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
      };

      setFormData({
        salary_structure_id: salaryConfig.salary_structure_id || "",
        custom_basic_salary: salaryConfig.custom_basic_salary || "",
        custom_hra_amount: salaryConfig.custom_hra_amount || "",
        custom_conveyance_amount: salaryConfig.custom_conveyance_amount || "",
        custom_education_allowance_amount:
          salaryConfig.custom_education_allowance_amount || "",
        custom_transport_allowance_amount:
          salaryConfig.custom_transport_allowance_amount || "",
        custom_fixed_allowance_amount:
          salaryConfig.custom_fixed_allowance_amount || "",
        custom_gratuity_amount: salaryConfig.custom_gratuity_amount || "",
        custom_commission_amount: salaryConfig.custom_commission_amount || "",
        custom_fuel_reimbursement: salaryConfig.custom_fuel_reimbursement || "",
        custom_telephone_reimbursement:
          salaryConfig.custom_telephone_reimbursement || "",
        custom_driver_reimbursement:
          salaryConfig.custom_driver_reimbursement || "",
        custom_vehicle_maintenance_reimbursement:
          salaryConfig.custom_vehicle_maintenance_reimbursement || "",
        custom_lta_reimbursement: salaryConfig.custom_lta_reimbursement || "",
        additional_allowance_1_name:
          salaryConfig.additional_allowance_1_name || "",
        additional_allowance_1_amount:
          salaryConfig.additional_allowance_1_amount || "",
        additional_allowance_2_name:
          salaryConfig.additional_allowance_2_name || "",
        additional_allowance_2_amount:
          salaryConfig.additional_allowance_2_amount || "",
        additional_allowance_3_name:
          salaryConfig.additional_allowance_3_name || "",
        additional_allowance_3_amount:
          salaryConfig.additional_allowance_3_amount || "",
        additional_deduction_1_name:
          salaryConfig.additional_deduction_1_name || "",
        additional_deduction_1_amount:
          salaryConfig.additional_deduction_1_amount || "",
        additional_deduction_2_name:
          salaryConfig.additional_deduction_2_name || "",
        additional_deduction_2_amount:
          salaryConfig.additional_deduction_2_amount || "",
        additional_deduction_3_name:
          salaryConfig.additional_deduction_3_name || "",
        additional_deduction_3_amount:
          salaryConfig.additional_deduction_3_amount || "",
        effective_from: formatDateForInput(salaryConfig.effective_from),
        effective_to: formatDateForInput(salaryConfig.effective_to),
        status: salaryConfig.status || "draft",
      });

      const structure = salaryStructures.find(
        (s) => s.id === salaryConfig.salary_structure_id,
      );
      setSelectedStructure(structure);
    }
  }, [salaryConfig, salaryStructures]);

  const handleStructureChange = (e) => {
    const structureId = e.target.value;
    setFormData({ ...formData, salary_structure_id: structureId });
    const structure = salaryStructures.find(
      (s) => s.id === parseInt(structureId),
    );
    setSelectedStructure(structure);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formattedData = {
      salary_structure_id: parseInt(formData.salary_structure_id),
      custom_basic_salary: parseFloat(formData.custom_basic_salary) || 0,
      custom_hra_amount: parseFloat(formData.custom_hra_amount) || 0,
      custom_conveyance_amount:
        parseFloat(formData.custom_conveyance_amount) || 0,
      custom_education_allowance_amount:
        parseFloat(formData.custom_education_allowance_amount) || 0,
      custom_transport_allowance_amount:
        parseFloat(formData.custom_transport_allowance_amount) || 0,
      custom_fixed_allowance_amount:
        parseFloat(formData.custom_fixed_allowance_amount) || 0,
      custom_gratuity_amount: parseFloat(formData.custom_gratuity_amount) || 0,
      custom_commission_amount:
        parseFloat(formData.custom_commission_amount) || 0,
      custom_fuel_reimbursement:
        parseFloat(formData.custom_fuel_reimbursement) || 0,
      custom_telephone_reimbursement:
        parseFloat(formData.custom_telephone_reimbursement) || 0,
      custom_driver_reimbursement:
        parseFloat(formData.custom_driver_reimbursement) || 0,
      custom_vehicle_maintenance_reimbursement:
        parseFloat(formData.custom_vehicle_maintenance_reimbursement) || 0,
      custom_lta_reimbursement:
        parseFloat(formData.custom_lta_reimbursement) || 0,
      additional_allowance_1_name: formData.additional_allowance_1_name || null,
      additional_allowance_1_amount:
        parseFloat(formData.additional_allowance_1_amount) || null,
      additional_allowance_2_name: formData.additional_allowance_2_name || null,
      additional_allowance_2_amount:
        parseFloat(formData.additional_allowance_2_amount) || null,
      additional_allowance_3_name: formData.additional_allowance_3_name || null,
      additional_allowance_3_amount:
        parseFloat(formData.additional_allowance_3_amount) || null,
      additional_deduction_1_name: formData.additional_deduction_1_name || null,
      additional_deduction_1_amount:
        parseFloat(formData.additional_deduction_1_amount) || null,
      additional_deduction_2_name: formData.additional_deduction_2_name || null,
      additional_deduction_2_amount:
        parseFloat(formData.additional_deduction_2_amount) || null,
      additional_deduction_3_name: formData.additional_deduction_3_name || null,
      additional_deduction_3_amount:
        parseFloat(formData.additional_deduction_3_amount) || null,
      effective_from: formData.effective_from,
      effective_to: formData.effective_to,
      status: formData.status,
    };

    const result = await dispatch(
      updateExistingSalaryConfiguration(salaryConfig.id, formattedData),
    );

    if (result.success) {
      onSuccess();
      onClose();
    }
    setLoading(false);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
      "&.Mui-focused": {
        color: "#f59e0b",
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && salaryConfig && (
        <>
          <style>
            {`
        .MuiPopover-root {
          z-index: 1400 !important;
        }
        .MuiPopper-root {
          z-index: 1400 !important;
        }
        .MuiMenu-root {
          z-index: 1400 !important;
        }
      `}
          </style>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100vh",
              width: "100%",
              maxWidth: "700px",
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={editSalaryConfigSvg}
                      alt="Edit Salary Configuration"
                      sx={{ width: 80, height: 80, objectFit: "contain" }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      Edit Salary Configuration
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Update salary details for {salaryConfig.first_name}{" "}
                      {salaryConfig.last_name}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3, scrollbarGutter: "stable" }}>
                <Stack spacing={3}>
                  {/* Structure Selection */}
                  <Accordion
                    defaultExpanded
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <PersonIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Salary Structure
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        fullWidth
                        select
                        label="Select Salary Structure"
                        name="salary_structure_id"
                        value={formData.salary_structure_id}
                        onChange={handleStructureChange}
                        required
                        sx={inputSx}
                        InputLabelProps={{ shrink: true }}
                      >
                        {salaryStructures.map((struct) => (
                          <MenuItem key={struct.id} value={struct.id}>
                            {struct.department_name} - {struct.level} Grade{" "}
                            {struct.grade}
                          </MenuItem>
                        ))}
                      </TextField>
                    </AccordionDetails>
                  </Accordion>

                  {/* Salary Components */}
                  <Accordion
                    defaultExpanded
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <MoneyIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Salary Components
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Basic Salary (₹)"
                            name="custom_basic_salary"
                            type="number"
                            value={formData.custom_basic_salary}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="HRA Amount (₹)"
                            name="custom_hra_amount"
                            type="number"
                            value={formData.custom_hra_amount}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Conveyance (₹)"
                            name="custom_conveyance_amount"
                            type="number"
                            value={formData.custom_conveyance_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Education Allowance (₹)"
                            name="custom_education_allowance_amount"
                            type="number"
                            value={formData.custom_education_allowance_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Transport Allowance (₹)"
                            name="custom_transport_allowance_amount"
                            type="number"
                            value={formData.custom_transport_allowance_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Fixed Allowance (₹)"
                            name="custom_fixed_allowance_amount"
                            type="number"
                            value={formData.custom_fixed_allowance_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Commission Amount (₹)"
                            name="custom_commission_amount"
                            type="number"
                            value={formData.custom_commission_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Additional Allowances */}
                  <Accordion
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <AddIcon sx={{ color: "#10b981" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Additional Allowances
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Allowance 1 Name"
                            name="additional_allowance_1_name"
                            value={formData.additional_allowance_1_name}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Amount (₹)"
                            name="additional_allowance_1_amount"
                            type="number"
                            value={formData.additional_allowance_1_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Allowance 2 Name"
                            name="additional_allowance_2_name"
                            value={formData.additional_allowance_2_name}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Amount (₹)"
                            name="additional_allowance_2_amount"
                            type="number"
                            value={formData.additional_allowance_2_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Allowance 3 Name"
                            name="additional_allowance_3_name"
                            value={formData.additional_allowance_3_name}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Amount (₹)"
                            name="additional_allowance_3_amount"
                            type="number"
                            value={formData.additional_allowance_3_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Additional Deductions */}
                  <Accordion
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <RemoveIcon sx={{ color: "#ef4444" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Additional Deductions
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Deduction 1 Name"
                            name="additional_deduction_1_name"
                            value={formData.additional_deduction_1_name}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Amount (₹)"
                            name="additional_deduction_1_amount"
                            type="number"
                            value={formData.additional_deduction_1_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Deduction 2 Name"
                            name="additional_deduction_2_name"
                            value={formData.additional_deduction_2_name}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Amount (₹)"
                            name="additional_deduction_2_amount"
                            type="number"
                            value={formData.additional_deduction_2_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Deduction 3 Name"
                            name="additional_deduction_3_name"
                            value={formData.additional_deduction_3_name}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Amount (₹)"
                            name="additional_deduction_3_amount"
                            type="number"
                            value={formData.additional_deduction_3_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Effective Period & Status */}
                  <Accordion
                    defaultExpanded
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <CalendarIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Period & Status
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Effective From"
                            name="effective_from"
                            type="date"
                            value={formData.effective_from}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Effective To"
                            name="effective_to"
                            type="date"
                            value={formData.effective_to}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            select
                            label="Status"
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          >
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </Box>

              {/* Footer Buttons */}
              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  variant="contained"
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Update Configuration"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditSalaryConfigurationModal;
